import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '@/components/ui/Button'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import { api } from '@/lib/api/client'
import { theme as defaultTheme } from '@/lib/theme'
import StippledBackground from '@/components/StippledBackground'

interface SharedArtifactSnapshot {
  id: string
  name: string
  content: string
  type: 'html'
  language: string
  description?: string
  createdAt: string
  originalUserId: string
  viewCount: number
  importCount: number
}

// Simplified TopNav for shared artifact page
interface SimpleTopNavProps {
  onThemeToggle: () => void
  onLoginClick: () => void
  isGuestMode: boolean
}

function SimpleTopNav({ onThemeToggle, isGuestMode, onLoginClick }: SimpleTopNavProps) {
  const { isDark, colors } = useThemeMode()

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '40px',
        backgroundColor: colors.navBg,
        borderBottom: `1px solid ${colors.navBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Logo */}
      <a
        href="https://honcho.chat"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: colors.navItemText,
          letterSpacing: '1px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <svg width="20" height="18" viewBox="0 0 87 77" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }}>
          <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
          <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
          <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
          <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
          <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
          <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
          <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
          <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
          <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
          <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
          <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
          <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
          <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
          <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
          <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
          <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
          <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
          <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
          <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
          <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
        </svg>
        Honcho Chat
      </a>

      {/* Right side controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Login button for guest users */}
        {isGuestMode && (
          <button
            onClick={onLoginClick}
            style={{
              padding: '4px 12px',
              backgroundColor: colors.visualAccent1,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.85'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            Sign in to Import
          </button>
        )}

        {/* Theme Toggle */}
        <button
          onClick={onThemeToggle}
          style={{
            padding: '4px 8px',
            background: 'transparent',
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: colors.primaryText,
            fontSize: '14px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          {isDark ? '🌙' : '☀︎'}
        </button>
      </div>
    </div>
  )
}

export default function SharedArtifactPage() {
  const { id } = useParams<{ id: string }>()
  const { colors, toggleTheme } = useThemeMode()
  const { user } = useUser()
  const navigate = useNavigate()
  const [artifact, setArtifact] = useState<SharedArtifactSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [importSuccess, setImportSuccess] = useState(false)
  const [showCode, setShowCode] = useState(false)

  useEffect(() => {
    if (!id) return

    async function fetchSharedArtifact() {
      try {
        setLoading(true)
        const apiUrl = import.meta.env.VITE_API_URL
        const response = await fetch(`${apiUrl}/api/artifacts/shared/${id}`)

        if (!response.ok) {
          if (response.status === 404) {
            setError('Artifact not found')
          } else {
            setError('Failed to load artifact')
          }
          return
        }

        const data = await response.json()
        setArtifact(data)
      } catch (err) {
        console.error('Error fetching shared artifact:', err)
        setError('Failed to load artifact')
      } finally {
        setLoading(false)
      }
    }

    fetchSharedArtifact()
  }, [id])

  const handleImport = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/auth/login?redirect=/artifacts/shared/${id}`)
      return
    }

    setImporting(true)
    try {
      const { error: importError } = await api.api.artifacts.import({ id: id! }).post()

      if (importError) {
        console.error('Import error:', importError)
        setError('Failed to import artifact')
        return
      }

      setImportSuccess(true)

      // Redirect to home after successful import
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (err) {
      console.error('Error importing artifact:', err)
      setError('Failed to import artifact')
    } finally {
      setImporting(false)
    }
  }

  const handleLoginClick = () => {
    navigate(`/auth/login?redirect=/artifacts/shared/${id}`)
  }

  if (loading) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav
          onThemeToggle={toggleTheme}
          isGuestMode={!user}
          onLoginClick={handleLoginClick}
        />
        <div
          style={{
            fontSize: defaultTheme.fontSize.lg,
            color: colors.primaryText,
            zIndex: 1,
          }}
        >
          Loading artifact...
        </div>
      </div>
    )
  }

  if (error || !artifact) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav
          onThemeToggle={toggleTheme}
          isGuestMode={!user}
          onLoginClick={handleLoginClick}
        />
        <div
          style={{
            fontSize: defaultTheme.fontSize.lg,
            color: colors.primaryText,
            zIndex: 1,
          }}
        >
          {error || 'Artifact not found'}
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Animated stars background */}
      <StippledBackground />

      {/* Top Navigation */}
      <SimpleTopNav
        onThemeToggle={toggleTheme}
        isGuestMode={!user}
        onLoginClick={handleLoginClick}
      />

      {/* Main Content Layout */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          paddingTop: '80px',
          paddingBottom: defaultTheme.spacing.xl,
          display: 'flex',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: defaultTheme.spacing.lg,
          padding: '80px 350px 40px 200px',
        }}
      >
        {/* Artifact Window - Main Content */}
        <div
          style={{
            width: '100%',
            maxWidth: '1000px',
            backgroundColor: colors.primaryBg,
            border: `2px solid ${colors.primaryBorder}`,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            height: 'calc(100vh - 160px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Window Title Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: colors.secondaryBg,
              borderBottom: `1px solid ${colors.primaryBorder}`,
              padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: defaultTheme.spacing.sm }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: defaultTheme.fontSize.md,
                  fontWeight: '500',
                  color: colors.primaryText,
                }}
              >
                {artifact.name}
              </span>
            </div>
            <div style={{ display: 'flex', gap: defaultTheme.spacing.sm }}>
              {importSuccess ? (
                <div
                  style={{
                    padding: `${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm}`,
                    backgroundColor: colors.successBg || colors.secondaryBg,
                    border: `1px solid ${colors.successBorder || colors.primaryAccent}`,
                    borderRadius: '4px',
                    color: colors.successText || colors.primaryText,
                    fontSize: defaultTheme.fontSize.xs,
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ✓ Imported!
                </div>
              ) : user ? (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  isLoading={importing}
                  style={{
                    fontSize: defaultTheme.fontSize.sm,
                    padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
                    backgroundColor: colors.primaryAccent,
                    color: '#000000',
                    border: 'none',
                    fontWeight: '600',
                  }}
                >
                  Import to Storage
                </Button>
              ) : (
                <Button
                  onClick={handleLoginClick}
                  style={{
                    fontSize: defaultTheme.fontSize.sm,
                    padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
                    backgroundColor: colors.primaryAccent,
                    color: '#000000',
                    border: 'none',
                    fontWeight: '600',
                  }}
                >
                  Sign in to Import
                </Button>
              )}
              <Button
                onClick={() => setShowCode(!showCode)}
                style={{
                  fontSize: defaultTheme.fontSize.xs,
                  padding: `${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm}`,
                  backgroundColor: 'transparent',
                  color: colors.secondaryText,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              >
                {showCode ? '< Hide Code' : '</> View Code'}
              </Button>
            </div>
          </div>

          {/* Artifact Content or Code View */}
          {showCode ? (
            <div
              style={{
                padding: defaultTheme.spacing.md,
                backgroundColor: colors.terminalBg || colors.secondaryBg,
                flex: 1,
                overflowY: 'auto',
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: defaultTheme.fontSize.sm,
                  color: colors.primaryText,
                  fontFamily: defaultTheme.fontFamily.mono,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: '1.6',
                }}
              >
                {artifact.content}
              </pre>
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                flex: 1,
                backgroundColor: '#ffffff',
                overflow: 'hidden',
              }}
            >
              <iframe
                srcDoc={artifact.content}
                title={artifact.name}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          )}
        </div>

        {/* Info Widget Sidebar */}
        <div
          style={{
            position: 'fixed',
            top: '80px',
            right: '24px',
            width: '280px',
            zIndex: 2,
          }}
        >
          {/* Artifact Info Widget */}
          <div
            style={{
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '0',
              padding: defaultTheme.spacing.md,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: defaultTheme.spacing.sm,
                fontSize: defaultTheme.fontSize.lg,
                fontWeight: '600',
                color: colors.primaryText,
              }}
            >
              {artifact.name}
            </h3>
            {artifact.description && (
              <p
                style={{
                  margin: 0,
                  marginBottom: defaultTheme.spacing.md,
                  fontSize: defaultTheme.fontSize.sm,
                  color: colors.secondaryText,
                  lineHeight: '1.5',
                }}
              >
                {artifact.description}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: defaultTheme.spacing.xs,
                fontSize: defaultTheme.fontSize.sm,
                color: colors.tertiaryText,
              }}
            >
              <div>📅 {new Date(artifact.createdAt).toLocaleDateString()}</div>
              <div>👁️ {artifact.viewCount} views</div>
              <div>📥 {artifact.importCount} imports</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
